import { z } from 'zod';
import { truckTypeSchema, ITruckType } from './truckType';

/**
 * Schema for validating truck type form data.
 * Extends the base truck type schema with form-specific validations.
 *
 * @property {string} code - Truck type code (max 5 characters)
 * @property {string} description - Description of the truck type (max 255 characters)
 * @property {number} [defaultNumberOfPallets] - Optional default pallet capacity
 * @property {number} [maxPayload] - Optional maximum payload weight in kilograms
 * @property {number} [weightOfTruck] - Optional weight of the empty truck in kilograms
 * @property {number} [tareWeight] - Optional tare weight in kilograms
 * @property {number} [percentageWarning] - Optional warning threshold percentage
 */
export const truckTypeFormSchema = truckTypeSchema.extend({});

/**
 * Default form schema for new truck types.
 * Makes all required fields optional with empty defaults.
 */
export const truckTypeDefaultFormSchema = truckTypeFormSchema.extend({
    code: z.string().max(5, 'Code can only be up to 5 characters long.').optional().default(''),
    description: z.string().max(255, 'Description can only be up to 255 characters long.').optional().default(''),
});

/**
 * TypeScript interface representing TruckType form values.
 * Generated from the Zod schema for type safety.
 */
export type ITruckTypeFormValues = z.infer<typeof truckTypeFormSchema>;

/**
 * Factory function to create form values for a truck type.
 * Handles both new and existing truck types.
 *
 * @param {ITruckType} [truckType] - Optional existing truck type to populate form
 * @returns {ITruckTypeFormValues} Form values with appropriate defaults
 */
export const TruckTypeFormValues = (truckType ?: ITruckType) : ITruckTypeFormValues => {
    // Handle the default case (new truck type)
    if (!truckType) {
        return truckTypeDefaultFormSchema.parse({});
    }

    // Transform the ITruckType data into ITruckTypeFormValues structure
    return truckTypeFormSchema.parse({
        ...truckType,
    });
};

/**
 * Validates truck type form values.
 * Logs any validation errors to the console.
 *
 * @param {ITruckTypeFormValues} formValues - The form values to validate
 * @returns {boolean} True if validation passes, false otherwise
 */
export const validateTruckTypeForm = (formValues : ITruckTypeFormValues) : boolean => {
    try {
        truckTypeFormSchema.parse(formValues);
        return true;
    } catch (error) {
        if (error instanceof z.ZodError) {
            // eslint-disable-next-line no-console
            console.error('Truck type form validation failed:', error.errors);
        } else {
            // eslint-disable-next-line no-console
            console.error('An unexpected error occurred during validation:', error);
        }
        return false;
    }
};
